import { useEffect, useMemo, useRef } from 'react';

import { isResourceName } from '@spotify/confidence-core-react';
import {
  useAttachments,
  useCreateAttachment,
} from '@spotify/plugin-confidence-attachments-react';
import {
  type ToolGroupEntry,
  createToolExtension,
  isCreatePulseTool,
  isPulseMutationTool,
} from '@spotify/plugin-confidence-chat-react';
import { useOptionalProjectContext } from '@spotify/plugin-confidence-projects-react';
import {
  type CapturedBlock,
  type PulseCaptureResult,
  usePulseCaptureNotifications,
} from '@spotify/plugin-confidence-pulse-react';

import { confidenceChatPlugin } from '../plugin';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const EMPTY_CAPTURES: CapturedBlock[] = [];

const getSourceChatName = (eventName?: string): string | undefined => {
  const eventPathIndex = eventName?.indexOf('/events/') ?? -1;
  return eventPathIndex >= 0 ? eventName?.slice(0, eventPathIndex) : undefined;
};

const getCaptureResultState = (
  resultSucceeded?: boolean,
): PulseCaptureResult['state'] => {
  if (resultSucceeded === undefined) return 'pending';
  return resultSucceeded ? 'successful' : 'failed';
};

type ParsedPulseMutationResult = {
  pulse: { name: string; displayName?: string };
  captures: CapturedBlock[];
};

const parsePulseMutationResult = (
  content: string,
): ParsedPulseMutationResult | undefined => {
  let result: unknown;
  try {
    result = JSON.parse(content);
  } catch {
    return undefined;
  }

  if (!isRecord(result) || !isRecord(result.pulse)) return undefined;
  const pulseName = result.pulse.name;
  const pulseDisplayName = result.pulse.display_name;
  if (typeof pulseName !== 'string') return undefined;

  const captures = Array.isArray(result.blocks)
    ? result.blocks.flatMap(block => {
        if (
          !isRecord(block) ||
          block.status !== 'captured' ||
          typeof block.block !== 'string' ||
          typeof block.display_name !== 'string'
        ) {
          return [];
        }

        return [
          {
            pulseName,
            pulseDisplayName:
              typeof pulseDisplayName === 'string'
                ? pulseDisplayName
                : undefined,
            blockName: block.block,
            blockDisplayName: block.display_name,
          },
        ];
      })
    : [];

  return {
    pulse: {
      name: pulseName,
      ...(typeof pulseDisplayName === 'string' && {
        displayName: pulseDisplayName,
      }),
    },
    captures,
  };
};

export const SaveToPulseToolCapture = ({
  entry,
}: {
  entry: ToolGroupEntry;
}) => {
  const projectName = useOptionalProjectContext()?.name;
  const { notifyPulseCreated, observeCaptureResult } =
    usePulseCaptureNotifications();
  const { createAttachment } = useCreateAttachment();
  const resultContent = entry.result?.content;
  const resultSucceeded = entry.result?.success;
  const captureResultState = getCaptureResultState(resultSucceeded);
  const isCreate = isCreatePulseTool(entry.tool);
  const createdAttachmentsRef = useRef(new Set<string>());
  const previousResultStateRef = useRef(captureResultState);
  const parsedResult = useMemo(
    () =>
      resultSucceeded && resultContent
        ? parsePulseMutationResult(resultContent)
        : undefined,
    [resultContent, resultSucceeded],
  );
  const captures = parsedResult?.captures ?? EMPTY_CAPTURES;

  const sourceChat = getSourceChatName(entry.eventName);
  const attachmentResource =
    projectName ??
    (sourceChat && isResourceName(sourceChat, 'chats')
      ? sourceChat
      : undefined);

  const { attachments: existingAttachments, isLoading: isLoadingAttachments } =
    useAttachments({
      resource: attachmentResource ?? '',
      pageSize: 100,
      skip: !attachmentResource,
    });

  useEffect(() => {
    if (isCreate) return;
    observeCaptureResult({
      captures,
      eventName: entry.eventName,
      state: captureResultState,
    });
  }, [
    captures,
    captureResultState,
    entry.eventName,
    isCreate,
    observeCaptureResult,
  ]);

  useEffect(() => {
    const pulseName = parsedResult?.pulse.name;
    const completedLiveCreation =
      previousResultStateRef.current === 'pending' &&
      captureResultState === 'successful';
    previousResultStateRef.current = captureResultState;
    if (!isCreate || !completedLiveCreation || !pulseName) return;
    notifyPulseCreated(pulseName);
  }, [captureResultState, isCreate, notifyPulseCreated, parsedResult]);

  useEffect(() => {
    if (!resultSucceeded) return;
    if (!attachmentResource || isLoadingAttachments) return;
    const pulseNames =
      isCreate && parsedResult
        ? [parsedResult.pulse.name]
        : [...new Set(captures.map(capture => capture.pulseName))];
    for (const pulseName of pulseNames) {
      if (createdAttachmentsRef.current.has(pulseName)) continue;
      if (existingAttachments?.some(attachment => attachment.uri === pulseName))
        continue;
      createdAttachmentsRef.current.add(pulseName);
      const displayName =
        parsedResult?.pulse.name === pulseName
          ? (parsedResult.pulse.displayName ?? '')
          : (captures.find(capture => capture.pulseName === pulseName)
              ?.pulseDisplayName ?? '');
      void createAttachment({
        resource: attachmentResource,
        uri: pulseName,
        displayName,
      }).catch(() => {});
    }
  }, [
    attachmentResource,
    captures,
    createAttachment,
    existingAttachments,
    isCreate,
    isLoadingAttachments,
    parsedResult,
    resultSucceeded,
  ]);

  return <>{entry.displayName ?? 'Save to pulse'}</>;
};

export const SaveToPulseToolExtension = confidenceChatPlugin.provide(
  createToolExtension({
    id: 'save-to-pulse',
    predicate: isPulseMutationTool,
    display: 'grouped',
    collapseConsecutiveCalls: false,
    render: () => null,
    renderLabel: entry => <SaveToPulseToolCapture entry={entry} />,
  }),
);
